import * as React from 'react';
import { useSearchParams } from 'react-router';
import { STORAGE_TYPES } from '@sites/constants/generic';
import { RESI_TEST_APP_BUILD_ENVS, RESI_TEST_APP_EMBED_VALUES } from '@sites/constants/resi-test-app';
import getStorage from '@sites/helpers/getStorage';
import { useEnvironment } from '../environmentContext';

type QueryContextState = {
  embedShareUrl: string;
  isEmbed: boolean;
  isTag: boolean;
};

const QueryContext = React.createContext<QueryContextState | null>(null);

export const STORAGE_EMBED_KEY = 'sites-resi-io-embed-view';
export const STORAGE_EMBED_VALUES = {
  EMBED: 'embed',
  DEFAULT: 'default',
} as const;

const QueryProvider = ({ children }: React.PropsWithChildren) => {
  const mounted = React.useRef(false);
  const [embedShareUrl, setEmbedShareUrl] = React.useState('');
  const [searchParams] = useSearchParams();
  const { setBuildEnvironment } = useEnvironment();
  const storage = getStorage(STORAGE_TYPES.SESSION_STORAGE);
  const embedSearchParam = React.useMemo(() => searchParams.get(STORAGE_EMBED_VALUES.EMBED), [searchParams]);
  const [embedSessionStorage, setEmbedSessionStorage] = React.useState(() => storage.getItem(STORAGE_EMBED_KEY));
  const isTagSearchParam = React.useMemo(() => Boolean(searchParams.get('isTag')), [searchParams]);

  const setSessionStorage = React.useCallback(
    (value: string) => {
      storage.setItem(STORAGE_EMBED_KEY, value);
      setEmbedSessionStorage(value);
    },
    [storage]
  );

  const setEmbedOrBuildEnvironment = React.useCallback(
    (event: MessageEvent) => {
      if (event.data?.sender === 'resi-sites-script') {
        setEmbedShareUrl(event.data?.data ?? '');
        return;
      }

      if (event.data === RESI_TEST_APP_BUILD_ENVS.INTEGRATION || event.data === RESI_TEST_APP_BUILD_ENVS.PRODUCTION) {
        setBuildEnvironment(event.data);
        return;
      }
      if (event.data === RESI_TEST_APP_EMBED_VALUES.TRUE || event.data === RESI_TEST_APP_EMBED_VALUES.FALSE) {
        setSessionStorage(
          event.data === RESI_TEST_APP_EMBED_VALUES.TRUE ? STORAGE_EMBED_VALUES.EMBED : STORAGE_EMBED_VALUES.DEFAULT
        );
        return;
      }
    },
    [setBuildEnvironment, setSessionStorage]
  );

  React.useEffect(() => {
    if (!mounted.current) {
      window.addEventListener('message', setEmbedOrBuildEnvironment);
      mounted.current = true;
    }
    return () => {
      window.removeEventListener('message', setEmbedOrBuildEnvironment);
    };
  }, [setEmbedOrBuildEnvironment]);

  const setEmbedView = React.useCallback(
    (value: boolean) => {
      if (embedSessionStorage === null) {
        setSessionStorage(value ? STORAGE_EMBED_VALUES.EMBED : STORAGE_EMBED_VALUES.DEFAULT);
      }
    },
    [embedSessionStorage, setSessionStorage]
  );

  React.useEffect(() => {
    setEmbedView(embedSearchParam === RESI_TEST_APP_EMBED_VALUES.TRUE);
  }, [embedSearchParam, setEmbedView]);

  const values = React.useMemo(() => {
    return {
      embedShareUrl,
      isEmbed: embedSessionStorage
        ? embedSessionStorage === STORAGE_EMBED_VALUES.EMBED
        : embedSearchParam === RESI_TEST_APP_EMBED_VALUES.TRUE,
      isTag: isTagSearchParam,
    };
  }, [isTagSearchParam, embedSearchParam, embedSessionStorage, embedShareUrl]);

  return <QueryContext.Provider value={values}>{children}</QueryContext.Provider>;
};

function useQueryParams() {
  const context = React.useContext(QueryContext);
  if (!context) {
    throw new Error('useQueryParams must be used inside a QueryProvider');
  }
  return context;
}

export { QueryContext, QueryProvider, useQueryParams };
export type { QueryContextState };
